import React, { useState } from 'react';
import { User } from '../lib/supabase';
import { dataService } from '../services/dataService';
import LoadingSpinner from './LoadingSpinner';

interface UserAccountManagerProps {
  currentUser: User;
  onUserUpdate: (updatedUser: User) => void;
  schools?: Array<{id: string; name: string}>;
  disableSchoolChange?: boolean;
}

interface AccountFormData {
  firstName: string;
  lastName: string;
  email: string;
  schoolId: string;
}

interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const UserAccountManager: React.FC<UserAccountManagerProps> = ({
  currentUser,
  onUserUpdate,
  schools = [],
  disableSchoolChange = false
}) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error'; text: string} | null>(null);

  // Profile form state
  const [profileData, setProfileData] = useState<AccountFormData>({
    firstName: currentUser.firstName || '',
    lastName: currentUser.lastName || '',
    email: currentUser.email || '',
    schoolId: currentUser.schoolId || ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!profileData.firstName.trim() || !profileData.lastName.trim()) {
      showMessage('error', 'First name and last name are required');
      return;
    }

    if (profileData.firstName.trim().length < 2) {
      showMessage('error', 'First name must be at least 2 characters long');
      return;
    }

    if (profileData.lastName.trim().length < 2) {
      showMessage('error', 'Last name must be at least 2 characters long');
      return;
    }

    try {
      setIsLoading(true);
      
      await dataService.updateUser(currentUser.id, {
        firstName: profileData.firstName.trim(),
        lastName: profileData.lastName.trim(),
        schoolId: profileData.schoolId || undefined
      });

      // Update the current user object
      const newUser: User = {
        ...currentUser,
        firstName: profileData.firstName.trim(),
        lastName: profileData.lastName.trim(),
        schoolId: profileData.schoolId || currentUser.schoolId
      };

      onUserUpdate(newUser);
      showMessage('success', 'Profile updated successfully');
    } catch (error) {
      console.error('Profile update error:', error);
      showMessage('error', 'Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Must contain at least one lowercase letter');
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('Must contain at least one number');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Must contain at least one special character');
    }
    
    return errors;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      showMessage('error', 'All password fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMessage('error', 'New password and confirmation do not match');
      return;
    }

    const passwordErrors = validatePassword(passwordData.newPassword);
    if (passwordErrors.length > 0) {
      showMessage('error', `Password validation failed: ${passwordErrors.join(', ')}`);
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      showMessage('error', 'New password must be different from current password');
      return;
    }

    try {
      setIsLoading(true);
      
      // For now, we'll just update the password via the dataService
      // In a real app, you'd want to verify the current password first
      await dataService.updateUser(currentUser.id, {
        password: passwordData.newPassword
      });

      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      showMessage('success', 'Password updated successfully');
    } catch (error) {
      console.error('Password update error:', error);
      showMessage('error', 'Failed to update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderProfileTab = () => (
    <form onSubmit={handleProfileUpdate} style={{ 
      padding: 'clamp(1.5rem, 4vw, 2rem)',
      animation: 'fadeInUp 0.5s ease-out'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(212, 175, 55, 0.3)'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <div>
          <h3 style={{ 
            margin: 0,
            color: '#FFD700',
            fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
            fontWeight: '700',
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            Profile Information
          </h3>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#9CA3AF' }}>
            Update your personal details
          </p>
        </div>
      </div>

      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{ animation: 'fadeInUp 0.5s ease-out 0.1s both' }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#D4AF37',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            First Name *
          </label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              border: '2px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '10px',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              background: 'rgba(26, 31, 46, 0.8)',
              color: '#ffffff',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#FFD700';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={{ animation: 'fadeInUp 0.5s ease-out 0.15s both' }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#D4AF37',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            Last Name *
          </label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              border: '2px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '10px',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              background: 'rgba(26, 31, 46, 0.8)',
              color: '#ffffff',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#FFD700';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={{ animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#9CA3AF',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            Email Address
          </label>
          <input
            type="email"
            value={profileData.email}
            readOnly
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              border: '2px solid rgba(107, 114, 128, 0.3)',
              borderRadius: '10px',
              fontSize: '0.9rem',
              background: 'rgba(17, 24, 39, 0.6)',
              color: '#6B7280',
              cursor: 'not-allowed',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {(currentUser.role === 'coach' || currentUser.role === 'headteacher') && schools.length > 0 && (
          <div style={{ animation: 'fadeInUp 0.5s ease-out 0.25s both' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: disableSchoolChange ? '#9CA3AF' : '#D4AF37',
              marginBottom: '0.5rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontFamily: "'DM Sans', sans-serif"
            }}>
              School
            </label>
            {disableSchoolChange ? (
              <div style={{
                width: '100%',
                padding: '0.875rem 1rem',
                backgroundColor: 'rgba(17, 24, 39, 0.6)',
                border: '2px solid rgba(107, 114, 128, 0.3)',
                borderRadius: '10px',
                fontSize: '0.9rem',
                color: '#ffffff',
                cursor: 'not-allowed',
                boxSizing: 'border-box'
              }}>
                {schools.find(s => s.id === profileData.schoolId)?.name || 'No school assigned'}
              </div>
            ) : (
              <select
                value={profileData.schoolId}
                onChange={(e) => setProfileData(prev => ({ ...prev, schoolId: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '2px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: '10px',
                  fontSize: '0.9rem',
                  transition: 'all 0.3s ease',
                  background: 'rgba(26, 31, 46, 0.8)',
                  color: '#ffffff',
                  cursor: 'pointer',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#FFD700';
                  e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="" style={{ background: '#1a1f2e', color: '#9CA3AF' }}>Select a school</option>
                {schools.map(school => (
                  <option key={school.id} value={school.id} style={{ background: '#1a1f2e', color: '#ffffff' }}>
                    {school.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div style={{ animation: 'fadeInUp 0.5s ease-out 0.3s both' }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#9CA3AF',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            Role
          </label>
          <div style={{
            padding: '0.875rem 1rem',
            backgroundColor: 'rgba(17, 24, 39, 0.6)',
            border: '2px solid rgba(107, 114, 128, 0.3)',
            borderRadius: '10px',
            fontSize: '0.9rem',
            color: '#9CA3AF',
            textTransform: 'capitalize',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: currentUser.role === 'admin' ? '#FFD700' : currentUser.role === 'headteacher' ? '#C41E3A' : '#2E8B57'
            }}></span>
            {currentUser.role}
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.875rem 2.5rem',
          background: 'linear-gradient(135deg, #D4AF37 0%, #FFD700 50%, #D4AF37 100%)',
          backgroundSize: '200% 200%',
          color: '#0A0E17',
          border: 'none',
          borderRadius: '10px',
          fontSize: '0.9rem',
          fontWeight: '700',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          opacity: isLoading ? 0.7 : 1,
          transition: 'all 0.3s ease',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
          animation: 'fadeInUp 0.5s ease-out 0.35s both'
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 55, 0.4)';
            e.currentTarget.style.backgroundPosition = '100% 0';
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.3)';
            e.currentTarget.style.backgroundPosition = '0 0';
          }
        }}
      >
        {isLoading && <LoadingSpinner />}
        {isLoading ? 'Updating...' : 'Update Profile'}
      </button>
    </form>
  );

  const renderPasswordTab = () => (
    <form onSubmit={handlePasswordChange} style={{ 
      padding: 'clamp(1.5rem, 4vw, 2rem)',
      animation: 'fadeInUp 0.5s ease-out'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, rgba(196, 30, 58, 0.2) 0%, rgba(196, 30, 58, 0.1) 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(196, 30, 58, 0.3)'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C41E3A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <circle cx="12" cy="16" r="1"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>
        <div>
          <h3 style={{ 
            margin: 0,
            color: '#FFD700',
            fontSize: 'clamp(1.25rem, 3vw, 1.5rem)',
            fontWeight: '700',
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}>
            Change Password
          </h3>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#9CA3AF' }}>
            Keep your account secure
          </p>
        </div>
      </div>

      <div style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        maxWidth: '450px',
        marginBottom: '2rem'
      }}>
        <div style={{ animation: 'fadeInUp 0.5s ease-out 0.1s both' }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#D4AF37',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            Current Password *
          </label>
          <input
            type="password"
            value={passwordData.currentPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              border: '2px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '10px',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              background: 'rgba(26, 31, 46, 0.8)',
              color: '#ffffff',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#FFD700';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>

        <div style={{ animation: 'fadeInUp 0.5s ease-out 0.15s both' }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#D4AF37',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            New Password *
          </label>
          <input
            type="password"
            value={passwordData.newPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
            required
            minLength={8}
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              border: '2px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '10px',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              background: 'rgba(26, 31, 46, 0.8)',
              color: '#ffffff',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#FFD700';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          <div style={{
            marginTop: '0.75rem',
            padding: '1rem',
            background: 'rgba(17, 24, 39, 0.6)',
            borderRadius: '10px',
            border: '1px solid rgba(107, 114, 128, 0.2)'
          }}>
            <p style={{ 
              margin: '0 0 0.5rem 0', 
              fontWeight: '600', 
              fontSize: '0.8rem',
              color: '#D4AF37',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>Password Requirements:</p>
            <ul style={{ 
              margin: 0, 
              paddingLeft: '1.25rem', 
              lineHeight: '1.6',
              fontSize: '0.8rem',
              color: '#9CA3AF'
            }}>
              <li>At least 8 characters long</li>
              <li>At least one uppercase letter (A-Z)</li>
              <li>At least one lowercase letter (a-z)</li>
              <li>At least one number (0-9)</li>
              <li>At least one special character (!@#$%^&*)</li>
            </ul>
          </div>
        </div>

        <div style={{ animation: 'fadeInUp 0.5s ease-out 0.2s both' }}>
          <label style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#D4AF37',
            marginBottom: '0.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontFamily: "'DM Sans', sans-serif"
          }}>
            Confirm New Password *
          </label>
          <input
            type="password"
            value={passwordData.confirmPassword}
            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: '0.875rem 1rem',
              border: '2px solid rgba(212, 175, 55, 0.3)',
              borderRadius: '10px',
              fontSize: '0.9rem',
              transition: 'all 0.3s ease',
              background: 'rgba(26, 31, 46, 0.8)',
              color: '#ffffff',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#FFD700';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.15)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={isLoading}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.875rem 2.5rem',
          background: 'linear-gradient(135deg, #8B2942 0%, #C41E3A 50%, #8B2942 100%)',
          backgroundSize: '200% 200%',
          color: '#ffffff',
          border: 'none',
          borderRadius: '10px',
          fontSize: '0.9rem',
          fontWeight: '700',
          cursor: isLoading ? 'not-allowed' : 'pointer',
          opacity: isLoading ? 0.7 : 1,
          transition: 'all 0.3s ease',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          boxShadow: '0 4px 15px rgba(196, 30, 58, 0.3)',
          animation: 'fadeInUp 0.5s ease-out 0.25s both'
        }}
        onMouseEnter={(e) => {
          if (!isLoading) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(196, 30, 58, 0.4)';
            e.currentTarget.style.backgroundPosition = '100% 0';
          }
        }}
        onMouseLeave={(e) => {
          if (!isLoading) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(196, 30, 58, 0.3)';
            e.currentTarget.style.backgroundPosition = '0 0';
          }
        }}
      >
        {isLoading && <LoadingSpinner />}
        {isLoading ? 'Updating...' : 'Change Password'}
      </button>
    </form>
  );

  return (
    <div style={{
      backgroundColor: '#111827',
      borderRadius: 'clamp(12px, 3vw, 20px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 1px rgba(212, 175, 55, 0.2)',
      border: '1px solid rgba(212, 175, 55, 0.15)',
      overflow: 'hidden',
      margin: 'clamp(0.5rem, 2vw, 1rem)',
      maxWidth: '100%',
      animation: 'fadeInUp 0.6s ease-out'
    }}>
      {/* Header */}
      <div style={{
        padding: 'clamp(1.5rem, 4vw, 2rem)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
        background: 'linear-gradient(135deg, rgba(10, 14, 23, 0.95) 0%, rgba(17, 24, 39, 0.9) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Animated background shimmer */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '200%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.03), transparent)',
          animation: 'shimmer 3s infinite',
          pointerEvents: 'none'
        }}></div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative', zIndex: 1 }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(196, 30, 58, 0.1) 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(212, 175, 55, 0.25)',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Z"/>
              <path d="M12 16v-4"/>
              <path d="M12 8h.01"/>
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 2v2"/>
              <path d="M12 20v2"/>
              <path d="m4.93 4.93 1.41 1.41"/>
              <path d="m17.66 17.66 1.41 1.41"/>
              <path d="M2 12h2"/>
              <path d="M20 12h2"/>
              <path d="m6.34 17.66-1.41 1.41"/>
              <path d="m19.07 4.93-1.41 1.41"/>
            </svg>
          </div>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: 'clamp(1.5rem, 5vw, 2rem)',
              fontWeight: '700',
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 50%, #FFD700 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              lineHeight: '1.2'
            }}>
              Account Management
            </h2>
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
              color: '#9CA3AF',
              lineHeight: '1.4'
            }}>
              Manage your account information and security
            </p>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div style={{
          padding: '1rem 1.5rem',
          background: message.type === 'success' 
            ? 'linear-gradient(135deg, rgba(46, 139, 87, 0.2) 0%, rgba(46, 139, 87, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(196, 30, 58, 0.2) 0%, rgba(196, 30, 58, 0.1) 100%)',
          borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
          color: message.type === 'success' ? '#2E8B57' : '#C41E3A',
          fontSize: '0.9rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <span style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            background: message.type === 'success' ? 'rgba(46, 139, 87, 0.2)' : 'rgba(196, 30, 58, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {message.type === 'success' ? '✓' : '!'}
          </span>
          {message.text}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
        backgroundColor: 'rgba(10, 14, 23, 0.5)',
        overflowX: 'auto'
      }}>
        {[
          { 
            key: 'profile', 
            label: 'Profile', 
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            )
          },
          { 
            key: 'password', 
            label: 'Password', 
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <circle cx="12" cy="16" r="1"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
            )
          }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            style={{
              flex: 1,
              minWidth: 'fit-content',
              padding: 'clamp(1rem, 3vw, 1.25rem) clamp(1.5rem, 4vw, 2rem)',
              border: 'none',
              background: activeTab === tab.key 
                ? 'linear-gradient(180deg, rgba(212, 175, 55, 0.1) 0%, transparent 100%)'
                : 'transparent',
              color: activeTab === tab.key ? '#FFD700' : '#6B7280',
              fontSize: 'clamp(0.8rem, 2vw, 0.9rem)',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              borderBottom: activeTab === tab.key 
                ? '3px solid #FFD700' 
                : '3px solid transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              whiteSpace: 'nowrap',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontFamily: "'DM Sans', sans-serif",
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#D4AF37';
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.key) {
                e.currentTarget.style.color = '#6B7280';
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && renderProfileTab()}
      {activeTab === 'password' && renderPasswordTab()}
    </div>
  );
};

export default UserAccountManager;